import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, Maximize2Icon } from 'lucide-react';
import { PageLightbox } from '../PageLightbox';
import { volumeThreeSlides } from '../../data/volumeThreeSlides';
import { useVolumeCarousel } from './useVolumeCarousel';
type VolumeCarouselProps = {
  autoplay?: boolean;
};
const ease = [0.23, 1, 0.32, 1] as const;
const roundButton = 'flex h-11 w-11 items-center justify-center rounded-full border border-cream-line bg-white text-ink shadow-card transition-[background-color,transform] duration-150 ease-out hover:bg-cream active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-cream';
export function VolumeCarousel({
  autoplay = true
}: VolumeCarouselProps) {
  const slides = volumeThreeSlides;
  const c = useVolumeCarousel(slides.length, autoplay);
  return <section aria-labelledby="volume-three-heading" className="w-full border-t border-cream-line bg-white text-ink" data-id="element-331" data-mp="38co5qcdwd2jn">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-24" data-id="element-332" data-mp="34ir4hximt6pc">
        <header className="mx-auto max-w-2xl text-center" data-id="element-333" data-mp="34h6cofnpc1v6">
          <p className="text-[13px] font-semibold text-gold-deep" data-id="element-334" data-mp="2zj4sfrejhwi4">
            Memory Care Activity Ready · Vol. 3: Hands with Purpose
          </p>
          <h2 id="volume-three-heading" className="mt-3 font-display text-3xl font-bold text-ink sm:text-5xl" data-id="element-335" data-mp="2vjvxwkboduli">
            Take a look inside Volume 3
          </h2>
        </header>

        <div role="region" aria-roledescription="carousel" aria-label="Volume 3 sample pages" tabIndex={0} {...c.regionHandlers} className="mt-10 rounded-3xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4 focus-visible:ring-offset-cream sm:mt-14" data-id="element-336" data-mp="3nblcu8lr20kl">
          <div ref={c.viewportRef} className="overflow-hidden py-4" aria-live={c.autoplaying ? 'off' : 'polite'} data-id="element-337" data-mp="a15sqcrkw0u0">
            <motion.ul className="flex cursor-grab touch-pan-y active:cursor-grabbing" style={{
            x: c.x,
            visibility: c.width ? 'visible' : 'hidden'
          }} drag="x" dragConstraints={c.dragConstraints} dragElastic={0.15} dragMomentum={false} onDrag={c.onDrag} onDragEnd={c.onDragEnd} data-id="element-338" data-mp="29a73bcar6nya">
              {slides.map((slide, i) => {
              const active = i === c.index;
              return <li key={slide.src} role="group" aria-roledescription="slide" aria-label={`${i + 1} of ${slides.length}: ${slide.title}`} style={{
                width: c.slideWidth
              }} className="shrink-0 px-2.5 sm:px-4" data-id="element-339" data-mp="1bmylpsdvhz1k" data-mpi={i}>
                    <motion.div initial={false} animate={{
                  scale: active ? 1 : 0.9,
                  opacity: active ? 1 : 0.6
                }} transition={c.reduceMotion ? {
                  duration: 0
                } : {
                  duration: 0.3,
                  ease
                }} data-id="element-340" data-mp="3awn8kpc6595k">
                      <button type="button" onClick={() => c.openLightbox(i)} tabIndex={active ? 0 : -1} aria-label={`Open full page: ${slide.title}`} className="relative block w-full cursor-zoom-in rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-4 focus-visible:ring-offset-cream" data-id="element-341" data-mp="148o2me5uau8u">
                        <img src={slide.src} alt={slide.caption} width={1200} height={1500} loading={i === 0 ? 'eager' : 'lazy'} draggable={false} className="aspect-[4/5] w-full select-none rounded-2xl border border-cream-line bg-cream object-contain shadow-card" data-id="element-342" data-mp="3tryeoh0bb5zo" />
                        {active && <span aria-hidden="true" className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-ink shadow-card" data-id="element-343" data-mp="8zkpqtywmjc7">
                            <Maximize2Icon className="h-4 w-4" data-id="element-344" data-mp="3hgfhpee5yjbi" />
                          </span>}
                      </button>
                      <p className="mx-auto mt-4 min-h-[3em] max-w-[34ch] text-center text-[15px] leading-snug text-ink-soft" data-id="element-345" data-mp="3ma0b0t4tap4d">
                        {slide.caption}
                      </p>
                    </motion.div>
                  </li>;
            })}
            </motion.ul>
          </div>

          <div className="mt-4 flex items-center justify-center gap-5" data-id="element-346" data-mp="16vg1oydkhlit">
            <button type="button" onClick={c.prev} aria-label="Previous slide" className={roundButton} data-id="element-347" data-mp="16tbxsgfo4mij">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-348" data-mp="wwgz9xjeam49" />
            </button>

            <div className="flex items-center gap-2" data-id="element-349" data-mp="ryp6z2eu8xzj">
              {slides.map((slide, i) => {
              const active = i === c.index;
              return <button key={slide.src} type="button" onClick={() => c.goTo(i)} aria-label={`Go to slide ${i + 1}: ${slide.title}`} aria-current={active ? 'true' : undefined} className="flex h-6 items-center rounded-full px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold" data-id="element-350" data-mp="j4spw20j6bqz" data-mpi={i}>
                    <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${active ? 'w-6 bg-ink' : 'w-2 bg-cream-dark hover:bg-ink-muted'}`} data-id="element-351" data-mp="3qoikppb7itso" />
                  </button>;
            })}
            </div>

            <button type="button" onClick={c.next} aria-label="Next slide" className={roundButton} data-id="element-352" data-mp="daoxuk4gmiu4">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-353" data-mp="ic4gy6n2uf9v" />
            </button>
          </div>
        </div>

        <div className="mt-10 flex justify-center" data-id="element-354" data-mp="8k8g4ntlwb1y">
          <a href="#" className="inline-flex items-center gap-2 rounded-full border border-ink/20 bg-white px-6 py-3 text-[15px] font-semibold text-ink transition-colors duration-150 hover:border-ink/40 hover:bg-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-cream" data-id="element-355" data-mp="8inp386i3a8u">
            <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-356" data-mp="2grc0arakyz54" />
            Download free sample (PDF)
          </a>
        </div>
      </div>

      <PageLightbox pages={slides} index={c.lightboxIndex} onClose={() => c.setLightboxIndex(null)} onChange={i => {
      c.setLightboxIndex(i);
      c.goTo(i);
    }} data-id="element-357" data-mp="2viugoq7cqvp0" />
    </section>;
}